import { Box } from '@missionlabs/smartagent-app-components'
import useHasFeature, { AppFeatures, AppSubFeatures } from 'hooks/useHasFeature'
import useSmartAgentAPI from 'hooks/useSmartAgentAPI'
import Search from 'images/search.svg'
import React, { useEffect, useRef, useState } from 'react'
import { H } from 'react-accessible-headings'
import { useDispatch, useSelector } from 'react-redux'
import { getCallRecording } from 'services/api/api.contact'
import { accessScreenRecording } from 'store/contactSearch/contactSearch.actions'
import { selectScreenRecordingURL } from 'store/contactSearch/contactSearch.selectors'
import { selectScoreInteraction } from 'store/qa/qa.selectors'
import RootState from 'store/state'
import { IInteraction } from 'views/Metrics/HistoricMetrics/types'
import { AudioPlayer } from './AudioPlayer'
import { AudioPlayerError, ICallRecordingData } from './interfaces/AudioPlayer'

interface Props {
    contactID?: string
    recordingDate?: number
    widgetTitle?: string
    recordings?: IInteraction['recordings']
}

export const CallRecording: React.FC<Props> = (props) => {
    const [haveDoneInitialLoad, setHaveDoneInitialLoad] = useState<boolean>(false)
    const [expirationErrorCount, setExpirationErrorCount] = useState<number>(0)
    const [requestCount, setRequestCount] = useState<number>(0)
    const [persistentElapsed, setPersistentElapsed] = useState<number | null>(null)
    const [hasCallRecording, setCallRecording] = useState(false)
    const [hasScreenRecording, setScreenRecording] = useState(false)

    // REFS
    const [containerRef, setContainerRef] =
        useState<React.MutableRefObject<HTMLDivElement | null> | null>(null)
    const [videoRef, setVideoRef] =
        useState<React.MutableRefObject<HTMLVideoElement | null> | null>(null)
    const [audioRef, setAudioRef] = useState<React.RefObject<HTMLAudioElement> | null>(null)
    const [ch1Muted, setCh1Muted] = useState(false)
    const [ch2Muted, setCh2Muted] = useState(false)

    // Refs for audio nodes
    const audioContextRef = useRef<AudioContext | null>(null)
    const sourceRef = useRef<MediaElementAudioSourceNode | null>(null)
    const splitterRef = useRef<ChannelSplitterNode | null>(null)
    const mergerRef = useRef<ChannelMergerNode | null>(null)
    const leftGainRef = useRef<GainNode | null>(null)
    const rightGainRef = useRef<GainNode | null>(null)

    const { contactID, recordingDate, contact } = useSelector<
        RootState,
        { contactID: string; recordingDate: number; contact: IInteraction }
    >((state) => ({
        contactID: props.contactID ?? state.contact?.ID ?? '',
        recordingDate: props.recordingDate ?? state.contact?.initiationTimestamp ?? 0,
        contact: state.contact as IInteraction,
    }))

    const recordings = props.recordings || contact?.recordings

    const dispatch = useDispatch()
    const hasFeature = useHasFeature()

    const [error, loading, response, request] = useSmartAgentAPI<ICallRecordingData, RootState>(
        async (state) =>
            getCallRecording(
                state.app.ID,
                state.app.instance!.ID,
                state.auth.token!,
                contactID,
                recordingDate,
            ),
        false,
    )

    /**
     * The function attempts to retrieve call recording data (up to 3 retries)
     */
    const fetchRecordingData = () => {
        if (requestCount <= 3) {
            request()
            setRequestCount((prevCount) => prevCount + 1)
        }
    }

    /**
     * The  function sets up an AudioContext and creates an audio graph for processing audio channels.
     * @returns The function returns nothing (`undefined`) if the `audioRef` is not available or not set.
     */
    const initAudioContext = () => {
        if (!audioRef?.current) return

        // create a new AudioContext if necessary
        const audioContext = new AudioContext()
        audioContextRef.current = audioContext

        const source = audioContext.createMediaElementSource(audioRef.current)
        sourceRef.current = source

        const splitter = audioContext.createChannelSplitter(2)
        const merger = audioContext.createChannelMerger(2)

        const leftGain = audioContext.createGain()
        const rightGain = audioContext.createGain()

        source.connect(splitter)
        splitter.connect(leftGain, 0)
        splitter.connect(rightGain, 1)
        leftGain.connect(merger, 0, 0)
        rightGain.connect(merger, 0, 1)
        merger.connect(audioContext.destination)

        // update refs for muting/unmuting
        splitterRef.current = splitter
        mergerRef.current = merger
        leftGainRef.current = leftGain
        rightGainRef.current = rightGain

        // set initial mute state for both channels
        leftGain.gain.setValueAtTime(ch1Muted ? 0 : 1, audioContext.currentTime)
        rightGain.gain.setValueAtTime(ch2Muted ? 0 : 1, audioContext.currentTime)
    }

    useEffect(() => {
        if (recordings) {
            setCallRecording(recordings.hasCallRecording)

            setScreenRecording(recordings.hasScreenRecording)
        }
    }, [recordings])

    useEffect(() => {
        if (contactID && hasFeature(AppFeatures.CALL_RECORDING)) {
            if (hasScreenRecording) {
                // fetch pre-signed url for screen recording to enable potential playback
                // action updates redux store with URL, if request fails store gets empty string
                dispatch(accessScreenRecording(contactID, recordingDate))
            }

            if (hasCallRecording) {
                // fetch call recording data
                // if requests fail more than 3 times, no audio player component is rendered
                fetchRecordingData()
            }
        }
    }, [contactID, hasScreenRecording, hasCallRecording])

    useEffect(() => {
        if (error) {
            fetchRecordingData()
        }
    }, [error])

    // initialises the audio context and audio graph once audio is available
    useEffect(() => {
        initAudioContext()

        return () => {
            if (audioContextRef.current) {
                if (audioContextRef.current.state !== 'closed') {
                    audioContextRef.current.close()?.catch((error) => {
                        console.warn('Error closing AudioContext:', error)
                    })
                }
            }
        }
    }, [audioRef])

    // updates gain nodes when muting/unmuting
    useEffect(() => {
        if (leftGainRef.current && rightGainRef.current && audioContextRef.current) {
            leftGainRef.current.gain.setValueAtTime(
                ch1Muted ? 0 : 1,
                audioContextRef.current.currentTime,
            )
            rightGainRef.current.gain.setValueAtTime(
                ch2Muted ? 0 : 1,
                audioContextRef.current.currentTime,
            )
        }
    }, [ch1Muted, ch2Muted])

    const onAudioError = (err: AudioPlayerError) => {
        const signedURLHasProbablyExpired = err.statusCode === 401 || err.statusCode === 403
        if (signedURLHasProbablyExpired) {
            setExpirationErrorCount(expirationErrorCount + 1)
            if (expirationErrorCount < 3) {
                //Prevent an endless loop if URL invalid.
                request()
            }
        }
    }

    if (loading && !haveDoneInitialLoad) {
        //Avoid unmounting the player on URL refreshes.
        return <p>{loading}</p>
    } else if (!haveDoneInitialLoad) {
        setHaveDoneInitialLoad(true)
    }

    const headerTitle = props.widgetTitle ? props.widgetTitle : 'Call Recording'
    const screenRecordingURL = useSelector(selectScreenRecordingURL)

    // FEATURE FLAGS - CALL RECORDING
    const { canDownload, canDeleteScreenRecording } = {
        canDownload: hasFeature(AppFeatures.CALL_RECORDING, AppSubFeatures.CALL_RECORDING_DOWNLOAD),
        canDeleteScreenRecording: hasFeature(
            AppFeatures.CALL_RECORDING,
            AppSubFeatures.SCREEN_RECORDING_DELETE,
        ),
    }

    return (!screenRecordingURL && !!error) || expirationErrorCount >= 3 ? (
        <Box alt collapse hidden={!!error} header={<H>{headerTitle}</H>}>
            <div className="sa-loading no-data">
                <img src={Search} alt="Search" width="24px" height="24px" />
                <p>Unable to retrieve call recording data</p>
            </div>
        </Box>
    ) : screenRecordingURL || response?.status === 200 ? (
        <AudioPlayer
            {...response?.data}
            screenRecordingURL={screenRecordingURL}
            hasAudio={hasCallRecording}
            contactID={contactID}
            recordingDate={recordingDate}
            featureFlags={{ canDownload, canDeleteScreenRecording }}
            onAudioError={onAudioError}
            widgetTitle={headerTitle}
            persistentElapsed={persistentElapsed}
            setPersistentElapsed={setPersistentElapsed}
            refs={{
                audioContextRef,
                leftGainRef,
                rightGainRef,
                audioRef,
                videoRef,
                containerRef,
                setContainerRef,
                setVideoRef,
                setAudioRef,
            }}
            mutedState={{ ch1Muted, setCh1Muted, ch2Muted, setCh2Muted }}
        />
    ) : null
}
